/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { ENVIRONMENT } from 'config';
import { MODEL_OPTIONS } from 'modules/dashboard/constants';
import { models } from 'powerbi-client';
import columnSchema from './columnSchema';

export const PROCESSING_LABELS = ['Please wait...', 'Analyzing data...', 'Almost there!'];

const PDV_COMPATIBLE_TYPES = new Set([
  'pivotTable',
  'lineClusteredColumnComboChart',
  'tableEx',
  'clusteredColumnChart',
  'lineStackedColumnComboChart',
  'Donut chart',
  'lineChart',
  'areaChart',
]);

const splitLines = (csv) => csv.trim().replaceAll('\r', '').split('\n');

export const getComponentId = (visualName) =>
  visualName
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-');

export const getModelProvider = (modelName) =>
  MODEL_OPTIONS.find((option) => option.value === modelName)?.provider;

export const getTokenLimitMessage = (tokenUsage) => {
  const isInputTokenLimitReached =
    tokenUsage.inputTokenCount + 1000 > ENVIRONMENT.AGENT_INPUT_TOKEN_THRESHOLD;
  const isOutputTokenLimitReached =
    tokenUsage.outputTokenCount > ENVIRONMENT.AGENT_OUTPUT_TOKEN_THRESHOLD;
  if (isInputTokenLimitReached && isOutputTokenLimitReached) {
    return 'You have no token credits remaining. Please try again later or contact support.';
  }
  if (isInputTokenLimitReached) {
    return 'You have no input token credits remaining. Please try again later or contact support.';
  }
  if (isOutputTokenLimitReached) {
    return 'You have no output token credits remaining. Please try again later or contact support.';
  }
  return '';
};

export const convertToPDV = (csvData) =>
  splitLines(csvData)
    .map((line) =>
      line
        .split(',')
        .map((cell) => {
          const num = parseFloat(cell);
          // eslint-disable-next-line no-restricted-globals
          return isNaN(num) || cell.includes('-') ? cell : Number(num);
        })
        .join('|')
    )
    .join('\n');

export const csvToJson = (csv, type) => {
  if (PDV_COMPATIBLE_TYPES.has(type)) {
    return convertToPDV(csv);
  }
  const [headerLine, ...rows] = splitLines(csv).filter(Boolean);

  const rawHeaders = headerLine?.split(',')?.map((h) => h.trim());
  if (rawHeaders[0] === '') rawHeaders[0] = 'Metric';

  // Create unique headers using the invisible character
  const seen = {};
  const modifiedHeaders = rawHeaders?.map((h) => {
    if (!seen[h]) {
      seen[h] = 1;
      return h;
    }
    const invisibleSpace = '​'.repeat(seen[h]);
    seen[h] += 1;
    return h + invisibleSpace;
  });
  return rows.map((row) => {
    const values = row?.split(',')?.map((v) => v.trim());
    const rowMap = new Map();

    modifiedHeaders?.forEach((key, index) => {
      const value = values[index];
      // eslint-disable-next-line no-restricted-globals
      const parsedValue = isNaN(Number(value)) || value === '' ? value : Number(value);
      rowMap.set(key, parsedValue);
    });
    return Object.fromEntries(rowMap);
  });
};

export const getNestedConfig = (schema, searchName, displayName = '') => {
  if (schema[searchName]) return schema[searchName];
  if (schema[displayName]) return schema[displayName];

  const matchingKey = Object.keys(schema).find((key) =>
    key
      .split(',')
      .map((p) => p.trim())
      .includes(searchName)
  );

  return schema[matchingKey];
};

export const getCeruleanColumnSchema = (name) => {
  const schema = getNestedConfig(columnSchema, name) ?? {};
  return Array.isArray(schema) ? Object.assign({}, ...schema.filter(Boolean)) : schema;
};

export const createColumnSchema = (data, name, displayName) => {
  const [headerLine] = splitLines(data).filter(Boolean);
  const rawHeaders = headerLine?.split(',')?.map((h) => h.trim());
  const pageSchemaPool = [...(getNestedConfig(columnSchema, name, displayName) || [])];

  return (
    rawHeaders
      ?.filter((head) => !head?.startsWith('Header_'))
      ?.reduce((acc, header) => {
        const targetIndex = pageSchemaPool.findIndex((item) => header in item);
        if (targetIndex !== -1) {
          const [matchedObject] = pageSchemaPool.splice(targetIndex, 1);
          Object.keys(matchedObject).forEach((key) => {
            if (rawHeaders.includes(key)) {
              acc[key] = matchedObject[key];
            }
          });
        }
        return acc;
      }, {}) || {}
  );
};

export const noDataFilteredData = (data) =>
  data
    ?.filter(
      (item) =>
        !Array.isArray(item.rows) ||
        !item.rows.some((row) => typeof row === 'object' && Object.hasOwn(row, 'no_data_message'))
    )
    ?.map((item) => {
      if (!Array.isArray(item.rows)) return item;
      const cleanedRows = item.rows
        ?.map((row) =>
          Object.fromEntries(Object.entries(row).filter(([key]) => !key.startsWith('Header_')))
        )
        ?.filter((row) => typeof row !== 'object' || Object.keys(row).length > 0);

      return { ...item, rows: cleanedRows };
    })
    ?.filter((item) => !Array.isArray(item.rows) || item.rows.length > 0);

/**
 * Export summarized data of the given power bi visuals, skipping the ones that fail
 */
export const exportVisuals = async (visuals) =>
  (
    await Promise.all(
      visuals.map(async (visual) => {
        try {
          const result = await visual.exportData(models.ExportDataType.Summarized);
          return {
            visualName: visual.name,
            visualTitle: visual.title,
            visualType: visual.type,
            data: result?.data,
            pageName: visual?.page?.displayName,
          };
        } catch (err) {
          return null;
        }
      })
    )
  ).filter(Boolean);

export const buildImageExportSettings = (stateString, activePageName) => ({
  format: 'PNG',
  powerBIReportConfiguration: {
    defaultBookmark: {
      state: stateString,
    },
    pages: [{ pageName: activePageName }],
  },
});
